import {PlayerStoreContext} from '@common/player/player-context';
import {handleYoutubeEmbedMessage} from '@common/player/providers/youtube/handle-youtube-embed-message';
import {useYoutubeProviderSrc} from '@common/player/providers/youtube/use-youtube-provider-src';
import {
  YoutubeCommand,
  YouTubeCommandArg,
  YoutubeInternalState,
  YoutubeProviderInternalApi,
} from '@common/player/providers/youtube/youtube-types';
import {useGlobalListeners} from '@react-aria/utils';
import {useCallback, useContext, useEffect, useRef} from 'react';
import {Track} from '@app/web-player/tracks/track';

// Imports para la interfaz global del reproductor
import {usePlayerActions} from '@common/player/hooks/use-player-actions';
import {usePlayerStore} from '@common/player/hooks/use-player-store';

export function YoutubeProvider() {
  const {addGlobalListener, removeAllGlobalListeners} = useGlobalListeners();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const youtubeApi = useCallback(
    <T extends keyof YouTubeCommandArg>(
      command: T,
      arg?: YouTubeCommandArg[T],
    ) =>
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({
          event: 'command',
          func: command,
          args: arg ? [arg] : undefined,
        }),
        '*',
      ),
    [],
  );

  const loadVideoById = useCallback(
    (videoId: string) => {
      youtubeApi(YoutubeCommand.CueAndPlay, videoId);
    },
    [youtubeApi],
  );

  const {initialVideoUrl, origin} = useYoutubeProviderSrc(loadVideoById);
  const store = useContext(PlayerStoreContext);

  const internalStateRef = useRef<YoutubeInternalState>({
    duration: 0,
    currentTime: 0,
    lastTimeUpdate: 0,
    playbackRate: 1,
    state: -1,
    playbackReady: false,
    buffered: 0,
    firedPlaybackEnd: false,
  });

  const registerApi = useCallback(() => {
    const internalProviderApi: YoutubeProviderInternalApi = {
      loadVideoById,
    };
    store.setState({
      providerApi: {
        play: () => youtubeApi(YoutubeCommand.Play),
        pause: () => youtubeApi(YoutubeCommand.Pause),
        stop: () => youtubeApi(YoutubeCommand.Stop),
        seek: (time: number) => {
          if (time !== internalStateRef.current.currentTime) {
            youtubeApi(YoutubeCommand.Seek, time);
          }
        },
        setVolume: (volume: number) => youtubeApi(YoutubeCommand.SetVolume, volume),
        setMuted: (muted: boolean) => {
          if (muted) youtubeApi(YoutubeCommand.Mute);
          else youtubeApi(YoutubeCommand.Unmute);
        },
        setPlaybackRate: (value: number) => youtubeApi(YoutubeCommand.SetPlaybackRate, value),
        setPlaybackQuality: (value: string) => youtubeApi(YoutubeCommand.SetPlaybackQuality, value),
        getCurrentTime: () => internalStateRef.current.currentTime,
        getSrc: () => internalStateRef.current.videoId,
        internalProviderApi,
      },
    });
  }, [store, loadVideoById, youtubeApi]);

  useEffect(() => {
    addGlobalListener(window, 'message', event => {
      const e = event as MessageEvent;
      if (e.origin === origin && e.source === iframeRef.current?.contentWindow) {
        handleYoutubeEmbedMessage(e, internalStateRef, iframeRef, store);
      }
    });
    registerApi();
    return () => removeAllGlobalListeners();
  }, [addGlobalListener, removeAllGlobalListeners, store, origin, registerApi]);

  // --- INICIO: Lógica para Global VibeTurnPlayer Controls ---
  const globalPlayerActions = usePlayerActions(); // Renombrado para evitar colisión si 'player' se usa localmente
  const isPlaying = usePlayerStore(s => s.isPlaying);

  useEffect(() => {
    console.log('[YoutubeProvider] Intentando exponer VibeTurnPlayer en window...');
    const controls = {
      play: () => {
        console.log('[window.VibeTurnPlayer.play] llamado desde YoutubeProvider');
        globalPlayerActions.play();
      },
      pause: () => {
        console.log('[window.VibeTurnPlayer.pause] llamado desde YoutubeProvider');
        globalPlayerActions.pause();
      },
      togglePlayPause: () => {
        console.log('[window.VibeTurnPlayer.togglePlayPause] llamado desde YoutubeProvider');
        if (isPlaying) {
          
          globalPlayerActions.pause();
          if ((window as any).Android && typeof (window as any).Android.notifyPlaybackPaused === 'function') {
            (window as any).Android.notifyPlaybackPaused();
            console.log('[VibeTurn DEBUG] Android.notifyPlaybackPaused() called');
          }else{
            console.log('[VibeTurn DEBUG] Android.notifyPlaybackPaused() not called');
          }
        } else {
          globalPlayerActions.play();
          if ((window as any).Android && typeof (window as any).Android.notifyPlaybackResumed === 'function') {
            (window as any).Android.notifyPlaybackResumed();
            console.log('[VibeTurn DEBUG] Android.notifyPlaybackResumed() called');
          }else{
            console.log('[VibeTurn DEBUG] Android.notifyPlaybackResumed() not called');
          }
        }
      },
      playNextTrack: () => {
        console.log('[window.VibeTurnPlayer.playNextTrack] llamado desde YoutubeProvider');
        globalPlayerActions.playNext(); // Asumiendo que la acción se llama playNext()
      },
      playPreviousTrack: () => {
        console.log('[window.VibeTurnPlayer.playPreviousTrack] llamado desde YoutubeProvider');
        globalPlayerActions.playPrevious(); // Asumiendo que la acción se llama playPrevious()
      },
      getPlayerStatus: () => {
        console.log('[window.VibeTurnPlayer.getPlayerStatus] llamado desde YoutubeProvider');
        return isPlaying ? 'playing' : 'paused';
      },
      // Aquí podrías añadir más funciones si las necesitas
    };
    (window as any).VibeTurnPlayer = controls;
    console.log('[YoutubeProvider] VibeTurnPlayer controls expuestos en window.VibeTurnPlayer');
    return () => {
      delete (window as any).VibeTurnPlayer;
      console.log('[YoutubeProvider] VibeTurnPlayer controls eliminados de window');
    };
  }, [globalPlayerActions, isPlaying]); // Dependencias del useEffect
  // --- FIN: Lógica para Global VibeTurnPlayer Controls ---

  if (!initialVideoUrl) {
    return null;
  }

  return (
    <iframe
      className="h-full w-full"
      ref={iframeRef}
      src={initialVideoUrl}
      allowFullScreen
      allow="autoplay; encrypted-media; picture-in-picture;"
      referrerPolicy="strict-origin-when-cross-origin"
      onLoad={() => {
        // window does not receive "message" events on safari without waiting a small amount of time for some reason
        setTimeout(() => {
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({event: 'listening'}),
            '*',
          );
          registerApi();
        });
      }}
    />
  );
}
